import type { DragEndEvent } from '@dnd-kit/core'
import { closestCenter, DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, rectSortingStrategy, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import {
    type ActorEvent,
    ARRANGEMENT,
    type Arrangement,
    CUSTOM_VIEW_LAYOUT,
    type CustomViewLayout,
    type FieldADTValue,
    type RecordLikeProtocol
} from '@lighthouse/core'
import type { FlowLayoutNode } from '@lighthouse/shared'
import { getRecordPoolKey, useAtomData, useFlowLayoutContext } from '@lighthouse/shared'
import { mergeRefs } from '@lighthouse/tools'
import { useStore } from 'jotai'
import React, { forwardRef, memo, useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context'
import { calculateNewSortValue, getEffectiveSortValue } from '../shared/sortUtils'
import { Item } from './Item'
import { SortableViewItem } from './SortableViewItem'
import { buildVerticalItemNodeData, geVerticalWidthSize } from './utils'

const Root = styled.div`
    position: relative;
`

const Row = styled.div<{ cols: number; gap: number; isGrid: boolean }>`
    display: ${({ isGrid }) => (isGrid ? 'grid' : 'block')};
    grid-template-columns: ${({ cols }) => `repeat(${cols}, minmax(0, 1fr))`};
    gap: ${({ gap }) => `${gap}px`};
`

const FlatGrid = styled.div<{ cols: number; gap: number }>`
    display: grid;
    grid-template-columns: ${({ cols }) => `repeat(${cols}, minmax(0, 1fr))`};
    gap: ${({ gap }) => `${gap}px`};
`

interface RecordWithIndex {
    recordId: string
    index: number
}

interface RenderRecordWithData extends RecordWithIndex {
    itemData: FlowLayoutNode
}

interface Props extends React.ComponentPropsWithoutRef<'div'> {
    node: FlowLayoutNode
    anchorId?: string
    title: string
    readonly?: boolean
    gap: number
    cols: number
    layout: CustomViewLayout
    arrangement: Arrangement
    itemWidth: number
    enableDrag?: boolean
    sortId?: string
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onUpdateRecord?: (recordId: string, content: RecordLikeProtocol['content']) => Promise<RecordLikeProtocol>
}

export const VerticalScrollView = memo(
    forwardRef<HTMLDivElement, Props>((props, ref) => {
        const {
            node,
            anchorId,
            title,
            readonly,
            gap,
            cols,
            layout,
            arrangement,
            itemWidth,
            enableDrag,
            sortId,
            onRecordClick,
            onUpdateRecord
        } = props
        const innerRef = useRef<HTMLDivElement | null>(null)
        const { blockData, recordPoints, recordPoolAtom, dataSource } = useViewBlockContext()
        const { id } = blockData
        const { id: dsId, schema } = dataSource
        const isFitArrangement = arrangement === ARRANGEMENT.fit
        const isGridLayout = layout === CUSTOM_VIEW_LAYOUT.grid && !isFitArrangement
        const widthSize = useMemo(() => geVerticalWidthSize(isFitArrangement, itemWidth), [isFitArrangement, itemWidth])
        const itemsPerRow = isGridLayout ? Math.max(1, Math.floor(cols)) : 1

        const store = useStore()

        // selector 返回数组而非 Map，因为 useAtomData 使用 fast-deep-equal（基础版）不支持 Map 比较
        const sortedRecordPoints = useAtomData(
            recordPoolAtom,
            useCallback(
                pool => {
                    if (!enableDrag) {
                        return recordPoints
                    }
                    return [...recordPoints].sort((a, b) => {
                        const recordA = pool.get(getRecordPoolKey(dsId, a.id))
                        const recordB = pool.get(getRecordPoolKey(dsId, b.id))
                        if (!recordA || !recordB) {
                            return 0
                        }
                        return getEffectiveSortValue(recordA, sortId) - getEffectiveSortValue(recordB, sortId)
                    })
                },
                [enableDrag, recordPoints, dsId, sortId]
            )
        )

        const renderRecords = useMemo(() => {
            return sortedRecordPoints.map((recordPoint, index) => ({
                recordId: recordPoint.id,
                index,
                itemData: buildVerticalItemNodeData({
                    node,
                    blockId: id,
                    recordId: recordPoint.id,
                    index,
                    readonly,
                    widthSize
                })
            }))
        }, [sortedRecordPoints, node, id, readonly, widthSize])

        const recordIds = useMemo(() => renderRecords.map(r => r.recordId), [renderRecords])

        const rows = useMemo(() => {
            const result: RenderRecordWithData[][] = []
            for (let i = 0; i < renderRecords.length; i += itemsPerRow) {
                result.push(renderRecords.slice(i, i + itemsPerRow))
            }
            return result
        }, [itemsPerRow, renderRecords])

        const sensors = useSensors(
            useSensor(MouseSensor, {
                activationConstraint: { distance: 8 }
            }),
            useSensor(TouchSensor, {
                activationConstraint: {
                    delay: 250,
                    tolerance: 5
                }
            })
        )

        const handleDragEnd = useCallback(
            (event: DragEndEvent) => {
                const { active, over } = event
                if (!over || active.id === over.id || !enableDrag || !sortId || !onUpdateRecord) {
                    return
                }

                const draggedId = String(active.id)
                const activeIndex = recordIds.indexOf(draggedId)
                const overIndex = recordIds.indexOf(String(over.id))
                if (activeIndex === -1 || overIndex === -1) {
                    return
                }

                const reordered = arrayMove(recordIds, activeIndex, overIndex)

                const pool = store.get(recordPoolAtom)
                const siblings = reordered
                    .filter(rid => rid !== draggedId)
                    .map(rid => {
                        const record = pool.get(getRecordPoolKey(dsId, rid))
                        return record ? { id: rid, record } : null
                    })
                    .filter((item): item is { id: string; record: RecordLikeProtocol } => Boolean(item))

                const insertIndex = reordered.indexOf(draggedId)
                const siblingsBeforeDragged = reordered.slice(0, insertIndex).filter(rid => rid !== draggedId).length

                const newSortValue = calculateNewSortValue(siblings, siblingsBeforeDragged, sortId)

                const updateContent: RecordLikeProtocol['content'] = {
                    [sortId]: {
                        ...schema[sortId],
                        value: newSortValue
                    }
                }

                onUpdateRecord(draggedId, updateContent)
            },
            [enableDrag, sortId, onUpdateRecord, recordIds, store, recordPoolAtom, schema, dsId]
        )

        const sortingStrategy = isGridLayout && itemsPerRow > 1 ? rectSortingStrategy : verticalListSortingStrategy

        const { selectedNodes } = useFlowLayoutContext()
        const isBlockSelected = useMemo(
            () => selectedNodes?.some(item => item.scope === node.scope && item.id === node.id),
            [selectedNodes, node.scope, node.id]
        )
        const dragActive = readonly ? true : isBlockSelected

        if (enableDrag) {
            return (
                <Root ref={mergeRefs([innerRef, ref])} id={anchorId}>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={recordIds} disabled={!dragActive} strategy={sortingStrategy}>
                            {isGridLayout ? (
                                <FlatGrid cols={itemsPerRow} gap={gap}>
                                    {renderRecords.map(({ recordId, itemData, index }) => (
                                        <SortableViewItem key={recordId} disabled={!dragActive} id={recordId}>
                                            <Item
                                                data-record-id={recordId}
                                                recordId={recordId}
                                                recordIndexPath={String(index)}
                                                readonly={readonly}
                                                data={itemData}
                                                title={title}
                                                onRecordClick={onRecordClick}
                                            />
                                        </SortableViewItem>
                                    ))}
                                </FlatGrid>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap }}>
                                    {renderRecords.map(({ recordId, itemData, index }) => (
                                        <SortableViewItem key={recordId} id={recordId}>
                                            <Item
                                                data-record-id={recordId}
                                                recordId={recordId}
                                                recordIndexPath={String(index)}
                                                readonly={readonly}
                                                data={itemData}
                                                title={title}
                                                onRecordClick={onRecordClick}
                                            />
                                        </SortableViewItem>
                                    ))}
                                </div>
                            )}
                        </SortableContext>
                    </DndContext>
                </Root>
            )
        }

        return (
            <Root ref={mergeRefs([innerRef, ref])} id={anchorId}>
                {rows.map((rowRecords, rowIndex) => {
                    const rowKey = rowRecords.map(v => v.recordId).join('|') || `${id}-row-${rowIndex}`
                    const rowStyle: React.CSSProperties = rowIndex < rows.length - 1 ? { marginBottom: gap } : {}
                    return (
                        <Row key={rowKey} cols={itemsPerRow} gap={gap} isGrid={isGridLayout} style={rowStyle}>
                            {rowRecords.map(({ recordId, itemData }, index) => {
                                return (
                                    <Item
                                        key={recordId}
                                        data-record-id={recordId}
                                        recordId={recordId}
                                        recordIndexPath={String(index)}
                                        readonly={readonly}
                                        data={itemData}
                                        title={title}
                                        onRecordClick={onRecordClick}
                                    />
                                )
                            })}
                        </Row>
                    )
                })}
            </Root>
        )
    })
)
